import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Tooltip,
  LinearProgress,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Calculate as CalculateIcon,
  TrendingUp as TrendIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customIndicatorApi, sisUtils } from '../../../services/sisApi';
import { CustomIndicator, CreateIndicatorRequest } from '../../../types/sis';

const CustomIndicators: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [editingIndicator, setEditingIndicator] = useState<CustomIndicator | null>(null);
  const [filters, setFilters] = useState({
    category: '',
    is_active: '',
    search: '',
    page: 1,
  });
  const [formData, setFormData] = useState<CreateIndicatorRequest>({
    code: '',
    name: '',
    description: '',
    data_type: 'integer',
    category: 'enrollment',
    unit: '',
    is_required: false,
    calculation_formula: '',
  });

  const queryClient = useQueryClient();

  // Fetch indicators
  const { data: indicatorsResponse, isLoading, error } = useQuery({
    queryKey: ['customIndicators', filters],
    queryFn: () => customIndicatorApi.getAll({
      category: filters.category || undefined,
      is_active: filters.is_active ? filters.is_active === 'true' : undefined,
      search: filters.search || undefined,
      page: filters.page,
      page_size: 12,
    }),
  });

  // Create/Update indicator mutation
  const createIndicatorMutation = useMutation({
    mutationFn: (data: CreateIndicatorRequest) => customIndicatorApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customIndicators'] });
      handleCloseDialog();
    },
  });

  const updateIndicatorMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CustomIndicator> }) =>
      customIndicatorApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customIndicators'] });
      handleCloseDialog();
    },
  });

  // Delete indicator mutation
  const deleteIndicatorMutation = useMutation({
    mutationFn: (id: number) => customIndicatorApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customIndicators'] });
    },
  });

  const indicators = indicatorsResponse?.data?.results || [];
  const totalPages = Math.ceil((indicatorsResponse?.data?.count || 0) / 12);

  const handleOpenDialog = (indicator?: CustomIndicator) => {
    if (indicator) {
      setEditingIndicator(indicator);
      setFormData({
        code: indicator.code,
        name: indicator.name,
        description: indicator.description || '',
        data_type: indicator.data_type,
        category: indicator.category,
        unit: indicator.unit || '',
        is_required: indicator.is_required,
        calculation_formula: indicator.calculation_formula || '',
      });
    } else {
      setEditingIndicator(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        data_type: 'integer',
        category: 'enrollment',
        unit: '',
        is_required: false,
        calculation_formula: '',
      });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingIndicator(null);
  };

  const handleSubmit = () => {
    if (editingIndicator) {
      updateIndicatorMutation.mutate({
        id: editingIndicator.id!,
        data: formData,
      });
    } else {
      createIndicatorMutation.mutate(formData);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this indicator?')) {
      deleteIndicatorMutation.mutate(id);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
      enrollment: 'primary',
      infrastructure: 'secondary',
      academic: 'success',
      staff: 'warning',
      finance: 'error',
      custom: 'info',
    };
    return colors[category] || 'default';
  };

  const getDataTypeIcon = (dataType: string) => {
    switch (dataType) {
      case 'integer':
      case 'decimal':
        return '📊';
      case 'boolean':
        return '✓';
      case 'date':
        return '📅';
      case 'json':
        return '{}';
      default:
        return '📝';
    }
  };

  return (
    <>
      {/* Header and Controls */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Custom Indicators</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create Indicator
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
          sx={{ minWidth: 200 }}
        />
        <TextField
          label="Category"
          variant="outlined"
          size="small"
          select
          value={filters.category}
          onChange={(e) => setFilters({ ...filters, category: e.target.value, page: 1 })}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All Categories</MenuItem>
          {sisUtils.getIndicatorCategories().map((cat) => (
            <MenuItem key={cat.value} value={cat.value}>
              {cat.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Status"
          variant="outlined"
          size="small"
          select
          value={filters.is_active}
          onChange={(e) => setFilters({ ...filters, is_active: e.target.value, page: 1 })}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="true">Active</MenuItem>
          <MenuItem value="false">Inactive</MenuItem>
        </TextField>
      </Box>

      {/* Loading */}
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load indicators: {(error as any)?.message || 'Unknown error'}
        </Alert>
      )}

      {/* Indicators Grid */}
      <Grid container spacing={3}>
        {indicators.map((indicator: any) => (
          <Grid item xs={12} sm={6} md={4} key={indicator.id}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                opacity: indicator.is_active ? 1 : 0.7,
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" component="h3" noWrap sx={{ mr: 1 }}>
                    {indicator.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ mr: 1 }}>
                      {getDataTypeIcon(indicator.data_type)}
                    </Typography>
                    <Chip
                      label={indicator.category}
                      color={getCategoryColor(indicator.category)}
                      size="small"
                    />
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Code: <strong>{indicator.code}</strong>
                </Typography>

                {indicator.description && (
                  <Typography variant="body2" sx={{ mb: 2 }} noWrap>
                    {indicator.description}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Chip 
                    label={indicator.data_type} 
                    variant="outlined" 
                    size="small" 
                  />
                  {indicator.unit && (
                    <Chip 
                      label={indicator.unit} 
                      variant="outlined" 
                      size="small" 
                    />
                  )}
                  {indicator.is_required && (
                    <Chip 
                      label="Required" 
                      color="error" 
                      variant="outlined" 
                      size="small" 
                    />
                  )}
                </Box>

                {!indicator.is_active && (
                  <Chip 
                    label="Inactive" 
                    color="error" 
                    variant="filled" 
                    size="small" 
                  />
                )}
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Box>
                  <Tooltip title="View Details">
                    <IconButton size="small">
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Calculate Values">
                    <IconButton size="small">
                      <CalculateIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="View Trends">
                    <IconButton size="small">
                      <TrendIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Edit">
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenDialog(indicator)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDelete(indicator.id!)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Empty State */}
      {!isLoading && indicators.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No custom indicators found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first custom indicator to start tracking specific metrics
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create Indicator
          </Button>
        </Box>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={filters.page}
            onChange={(_, page) => setFilters({ ...filters, page })}
            color="primary"
          />
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog 
        open={open} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {editingIndicator ? 'Edit Indicator' : 'Create Custom Indicator'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  helperText="Use uppercase letters, numbers, and underscores only"
                  error={!sisUtils.validateIndicatorCode(formData.code) && formData.code !== ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Data Type"
                  value={formData.data_type}
                  onChange={(e) => setFormData({ ...formData, data_type: e.target.value as any })}
                >
                  {sisUtils.getDataTypes().map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                >
                  {sisUtils.getIndicatorCategories().map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Unit"
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  placeholder="e.g., students, %, currency"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_required}
                      onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                    />
                  }
                  label="Required Indicator"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Calculation Formula"
                  multiline
                  rows={3}
                  value={formData.calculation_formula}
                  onChange={(e) => setFormData({ ...formData, calculation_formula: e.target.value })}
                  placeholder="Optional: Python expression for calculated indicators"
                  helperText="Use field names and mathematical operators to define calculated indicators"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !formData.code || 
              !formData.name || 
              !sisUtils.validateIndicatorCode(formData.code) ||
              createIndicatorMutation.isPending ||
              updateIndicatorMutation.isPending
            }
          >
            {editingIndicator ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomIndicators;